/** Gold sheen sweep duration (seconds in CSS). */
export const LINE_MATCH_SHIMMER_MS = 1000;

/** Total effect lifetime — overlays + toast auto-unmount. */
export const LINE_MATCH_EFFECT_MS = 1500;

/** @deprecated use {@link LINE_MATCH_EFFECT_MS} */
export const LINE_MATCH_HIGHLIGHT_MS = LINE_MATCH_EFFECT_MS;

/** @deprecated use {@link LINE_MATCH_EFFECT_MS} */
export const LINE_MATCH_TOAST_MS = LINE_MATCH_EFFECT_MS;

const GOLD_SHEEN_GRADIENT =
    "linear-gradient(135deg, rgba(255,255,255,0) 30%, rgba(255,223,0,0.42) 50%, rgba(255,255,255,0) 70%)";

/** Visual cell indices in row-major order for row `rowIndex`. */
export function getVisualCellsForRow(rowIndex, gridSize) {
    const cells = [];
    for (let col = 0; col < gridSize; col += 1) {
        cells.push(rowIndex * gridSize + col);
    }
    return cells;
}

/** Visual cell indices in row-major order for column `colIndex`. */
export function getVisualCellsForColumn(colIndex, gridSize) {
    const cells = [];
    for (let row = 0; row < gridSize; row += 1) {
        cells.push(row * gridSize + colIndex);
    }
    return cells;
}

/**
 * Returns row/column indices where every tile sits in its solved target cell
 * (`layout[visualIdx] === visualIdx`, including the empty hole when applicable).
 */
export function computeMatchedLineIndices(layout, gridSize) {
    const n = gridSize * gridSize;
    if (!Array.isArray(layout) || layout.length !== n || gridSize < 1) {
        return { rows: [], cols: [] };
    }

    const rows = [];
    const cols = [];

    for (let row = 0; row < gridSize; row += 1) {
        let rowComplete = true;
        for (let col = 0; col < gridSize; col += 1) {
            const visualIdx = row * gridSize + col;
            if (Number(layout[visualIdx]) !== visualIdx) {
                rowComplete = false;
                break;
            }
        }
        if (rowComplete) {
            rows.push(row);
        }
    }

    for (let col = 0; col < gridSize; col += 1) {
        let colComplete = true;
        for (let row = 0; row < gridSize; row += 1) {
            const visualIdx = row * gridSize + col;
            if (Number(layout[visualIdx]) !== visualIdx) {
                colComplete = false;
                break;
            }
        }
        if (colComplete) {
            cols.push(col);
        }
    }

    return { rows, cols };
}

/**
 * Map visual cell index → highlight stamp (used to restart CSS animation on re-match).
 * @param {number|string} [stampSuffix] unique suffix so React remounts overlays
 */
export function buildHighlightCellMap(rows, cols, gridSize, stampSuffix = "") {
    const suffix = stampSuffix === "" || stampSuffix == null ? "" : `-${stampSuffix}`;
    const map = {};
    rows.forEach((rowIndex) => {
        getVisualCellsForRow(rowIndex, gridSize).forEach((visualIdx) => {
            map[visualIdx] = `row-${rowIndex}${suffix}`;
        });
    });
    cols.forEach((colIndex) => {
        getVisualCellsForColumn(colIndex, gridSize).forEach((visualIdx) => {
            const colStamp = `col-${colIndex}${suffix}`;
            map[visualIdx] = map[visualIdx] ? `${map[visualIdx]}|${colStamp}` : colStamp;
        });
    });
    return map;
}

/** `row` → horizontal sweep, `col` → vertical, `both` → diagonal. */
export function getLineMatchSweepKind(stamp = "") {
    const hasRow = /(?:^|\|)row-/.test(stamp);
    const hasCol = /(?:^|\|)col-/.test(stamp);
    if (hasRow && hasCol) {
        return "both";
    }
    if (hasCol) {
        return "col";
    }
    return "row";
}

/** Inset from the board edge so large toast text is not clipped (percent). */
const LINE_MATCH_TOAST_EDGE_PAD = 2.5;

/**
 * Center position (percent) for a floating toast over a row or column.
 * Edge rows/columns anchor flush inward so wide “Good Job!” text stays fully visible.
 */
export function getLineMatchToastPosition(kind, index, gridSize) {
    const lineStart = (index / gridSize) * 100;
    const lineEnd = ((index + 1) / gridSize) * 100;
    const center = (lineStart + lineEnd) / 2;
    const pad = LINE_MATCH_TOAST_EDGE_PAD;
    const isFirst = index === 0;
    const isLast = index === gridSize - 1;

    if (kind === "row") {
        return {
            top: isFirst
                ? `${lineStart + pad}%`
                : isLast
                  ? `${lineEnd - pad}%`
                  : `${center}%`,
            left: "50%",
            anchorX: "-50%",
            anchorY: isFirst ? "0%" : isLast ? "-100%" : "-50%",
        };
    }

    return {
        top: "50%",
        left: isFirst ? `${lineStart + pad}%` : isLast ? `${lineEnd - pad}%` : `${center}%`,
        anchorX: isFirst ? "0%" : isLast ? "-100%" : "-50%",
        anchorY: "-50%",
    };
}

const sharedKeyframes = {
    "@keyframes slidingLineMatchRim": {
        "0%": { opacity: 0 },
        "18%": { opacity: 1 },
        "100%": { opacity: 0 },
    },
    "@keyframes slidingLineMatchSheenRow": {
        "0%": { backgroundPosition: "120% 50%", opacity: 0 },
        "12%": { opacity: 1 },
        "100%": { backgroundPosition: "-20% 50%", opacity: 0 },
    },
    "@keyframes slidingLineMatchSheenCol": {
        "0%": { backgroundPosition: "50% 120%", opacity: 0 },
        "12%": { opacity: 1 },
        "100%": { backgroundPosition: "50% -20%", opacity: 0 },
    },
    "@keyframes slidingLineMatchSheenBoth": {
        "0%": { backgroundPosition: "120% 120%", opacity: 0 },
        "12%": { opacity: 1 },
        "100%": { backgroundPosition: "-20% -20%", opacity: 0 },
    },
    "@keyframes slidingLineMatchSheenReduced": {
        "0%": { opacity: 0 },
        "25%": { opacity: 0.75 },
        "100%": { opacity: 0 },
    },
};

/**
 * Premium gold shimmer overlay for one tile in a matched row/column.
 * @param {string} stamp highlight stamp from {@link buildHighlightCellMap}
 */
export function lineMatchHighlightOverlaySx(stamp = "") {
    const sweepKind = getLineMatchSweepKind(stamp);
    const sheenAnimation =
        sweepKind === "col"
            ? "slidingLineMatchSheenCol 1s cubic-bezier(0.25, 0.1, 0.25, 1) forwards"
            : sweepKind === "both"
              ? "slidingLineMatchSheenBoth 1s cubic-bezier(0.25, 0.1, 0.25, 1) forwards"
              : "slidingLineMatchSheenRow 1s cubic-bezier(0.25, 0.1, 0.25, 1) forwards";

    return {
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        zIndex: 4,
        overflow: "hidden",
        contain: "strict",
        willChange: "opacity",
        "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            boxShadow:
                "inset 0 0 0 1.5px rgba(255, 215, 0, 0.65), inset 0 0 14px rgba(255, 193, 7, 0.28), 0 0 10px rgba(255, 179, 0, 0.22)",
            animation: "slidingLineMatchRim 1s ease-out forwards",
            pointerEvents: "none",
        },
        "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: GOLD_SHEEN_GRADIENT,
            backgroundSize: "280% 280%",
            backgroundRepeat: "no-repeat",
            animation: sheenAnimation,
            pointerEvents: "none",
            mixBlendMode: "soft-light",
        },
        ...sharedKeyframes,
        "@media (prefers-reduced-motion: reduce)": {
            "&::after": {
                animation: "slidingLineMatchSheenReduced 0.9s ease-out forwards",
                mixBlendMode: "normal",
            },
        },
    };
}

/** Floating “Good Job!” label — fade in, bounce, float up, fade out (~1.5s). */
export function lineMatchToastSx(position) {
    const anchorX = position.anchorX ?? "-50%";
    const anchorY = position.anchorY ?? "-50%";

    return {
        position: "absolute",
        top: position.top,
        left: position.left,
        zIndex: 6,
        pointerEvents: "none",
        "--line-match-toast-x": anchorX,
        "--line-match-toast-y": anchorY,
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
        fontWeight: 800,
        fontSize: "clamp(2.625rem, 9vw, 3.1875rem)",
        letterSpacing: "0.06em",
        textTransform: "none",
        whiteSpace: "nowrap",
        backgroundImage: "linear-gradient(180deg, #fff9e6 0%, #ffe082 35%, #ffb300 70%, #ff8f00 100%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        filter: "drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 12px rgba(255, 193, 7, 0.35))",
        animation: "slidingLineMatchToastPremium 1.5s cubic-bezier(0.22, 1, 0.36, 1) forwards",
        "@keyframes slidingLineMatchToastPremium": {
            "0%": {
                opacity: 0,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(8%) scale(0.82)",
            },
            "14%": {
                opacity: 1,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(-6%) scale(1.1)",
            },
            "24%": {
                opacity: 1,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(-8%) scale(1)",
            },
            "62%": {
                opacity: 1,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(-18%) scale(1)",
            },
            "100%": {
                opacity: 0,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(-38%) scale(0.94)",
            },
        },
        "@keyframes slidingLineMatchToastReduced": {
            "0%": {
                opacity: 0,
                transform: "translate(var(--line-match-toast-x), var(--line-match-toast-y))",
            },
            "20%": {
                opacity: 1,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(-8%)",
            },
            "100%": {
                opacity: 0,
                transform:
                    "translate(var(--line-match-toast-x), var(--line-match-toast-y)) translateY(-22%)",
            },
        },
        "@media (prefers-reduced-motion: reduce)": {
            animation: "slidingLineMatchToastReduced 1.2s ease-out forwards",
        },
    };
}
